import {
    ComponentKind,
    ComponentDescription,
    TableGlideType,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    getPrimitiveColumns,
} from "../description";
import { ComponentDescriptor, ComponentConfiguratorContext, ComponentBuilder } from ".";
import { ComponentHandlerBase } from "./handler";
import { ColumnPropertyHandler, ColumnPropertyFlag } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { makeText, setColumnInRowForEditComponent } from "../builder-utils";
import { makeTextPropertyDescriptor } from "./descriptor-utils";

const ComponentKindLike: ComponentKind = "like";

const columnPropertyHandler = new ColumnPropertyHandler<ComponentDescription>(
    "dataColumnName",
    "Column",
    [ColumnPropertyFlag.Required, ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp],
    undefined,
    ["like", "thumbs"],
    getPrimitiveColumns,
    "number",
    PropertySection.Data
);

interface LikeComponentDescription extends ComponentDescription {
    readonly caption: PropertyDescription | undefined;
    readonly dataColumnName: PropertyDescription;
}

export class LikeComponentHandler extends ComponentHandlerBase<LikeComponentDescription> {
    constructor() {
        super(ComponentKindLike);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public get isNonEmptyValidationStopper(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: LikeComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        _searchVariable: SerializedComponent | undefined,
        _searchNotActive: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = columnPropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const caption = makeText(builder, desc.caption, tables.input, true);

        return b.let(
            lets,
            b.ui("Like", {
                value: valueToModify,
                onChange: action,
                isEnabled,
                validFlag: mutatingScreenKind !== undefined ? validFlag : undefined,
                caption,
            })
        );
    }

    public getDescriptor(
        _desc: LikeComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<LikeComponentDescription> {
        return {
            name: "Like",
            description: "Like or dislike an item",
            img: "thumbsUp",
            group: "Pickers",
            helpUrl: getDocURL("like"),
            isLegacy: !getFeatureSetting("likeComponent"),
            properties: [
                makeTextPropertyDescriptor(
                    "caption",
                    "Title",
                    "Enter the component's title",
                    false,
                    mutatingScreenKind,
                    {
                        propertySection: PropertySection.Top,
                        searchable: false,
                        emptyByDefault: true,
                    }
                ),
                columnPropertyHandler,
                ...this.getBasePropertyDescriptors(),
            ],
        };
    }
}
